<script lang="ts">
  /**
   * SetsDiscoveredSheet — the ledger of every named swim set the swimmer has
   * actually done. Opens from the History tab's "Sets you've swum" row via
   * { kind: "sets-discovered" }. Follows the SetDetailSheet overlay pattern
   * (scrim + sheet, lock/unlock body scroll, Escape-to-close), but renders as a
   * bottom sheet rather than full-screen.
   *
   * Each row shows:
   *   PRIMARY   — a color stroke-ribbon (the set's visual identity via SetGroupRibbon);
   *               skipped for the generic single-stroke sets (Freestyle set /
   *               Stroke Set), whose one-color ribbon read as a render failure
   *   SECONDARY — bold name (with ◆ if signature), then muted "N swims · First: Month Year"
   *   TIMES     — "Set time: first 4:31 · fastest 3:16 · last 4:10" (or the
   *               average length for variable-distance sets) from the swimFacts
   *               v2 cache, the same rows the Scores "Your sets" cards show
   *
   * The plainDefinition is intentionally dropped — the spec says the definition
   * moves to the future drill overlay (a separate next step, not in this build).
   *
   * Ribbon data path: row.firstSwumWorkoutId → bridge.getWorkoutFirehose →
   * deriveWorkout → find SetGroup matching row.setId → pass to SetGroupRibbon.
   * Falls back to text-only row if the firehose or matching SetGroup is absent.
   *
   * health copy: association only, no causation claims.
   */
  import { setsDiscoveredLedger, fmtMonthYear, type LedgerRow } from "../lib/setsDiscovered";
  import { closeOverlay, openOverlay } from "../lib/overlay";
  import { tapGuard } from "../lib/actions/tapGuard";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { bridge } from "../bridge/client";
  import { deriveWithSavedLabels } from "../lib/savedLabels";
  import type { SetGroup } from "../lib/lapAnalysis";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";
  import { strokeColor } from "../lib/designTokens";
  import { getAllSwimFacts } from "../lib/allTimeAggregates";
  import { setHistoryRows, fmtSetValue, type SetHistoryRow } from "../lib/setHistory";

  interface Props {
    open?: boolean;
  }
  let { open = false }: Props = $props();

  function close() {
    closeOverlay("sets-discovered");
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  /** Tap a row → open that set's page (single-slot system closes this ledger; its Back pill returns here). */
  function openDrill(setId: string) {
    openOverlay({ kind: "set-history", setId, from: "sets-discovered" });
  }

  /** The generic single-stroke gem renders as ONE solid band (+×N count) rather
   *  than a wall of identical chips. Only this set gets the band treatment. */
  const SINGLE_STROKE_SET_ID = "singleStrokeBlock";

  /** True when the ledger contains the single-stroke gem — drives the subtitle
   *  copy so it describes what's actually on screen (band vs. chip ribbon). */
  let hasSingleStroke = $derived(
    $setsDiscoveredLedger.some((r) => r.setId === SINGLE_STROKE_SET_ID),
  );

  /** Per-set first / fastest / last, re-read from the cache each time the sheet opens. */
  let history = $derived.by((): Record<string, SetHistoryRow> => {
    if (!open) return {};
    return Object.fromEntries(setHistoryRows(getAllSwimFacts()).map((r) => [r.setId, r]));
  });

  /** "Set time: first 4:31 · fastest 3:16 · last 4:10" / "Average 25: first 37.1s · …" */
  function timesLine(h: SetHistoryRow): string {
    const basis =
      h.basis === "setTime"
        ? "Set time"
        : `Average ${h.poolLengthMeters === null ? "length" : h.poolLengthMeters}`;
    if (h.timesSwum === 1) return `${basis}: ${fmtSetValue(h, h.first.valueS)}`;
    return `${basis}: first ${fmtSetValue(h, h.first.valueS)} · fastest ${fmtSetValue(h, h.fastest.valueS)} · last ${fmtSetValue(h, h.last.valueS)}`;
  }

  function onRowKey(e: KeyboardEvent, setId: string) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDrill(setId);
    }
  }

  // Lock background scroll while open (mirrors SetDetailSheet).
  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  /**
   * Resolved SetGroup per row — keyed by setId.
   * Populated reactively as the sheet opens and ribbon data is resolved.
   * Falls back gracefully: if no firehose or no matching SetGroup, the entry
   * stays absent and the row renders text-only (no crash).
   */
  let resolvedSetGroups = $state<Record<string, SetGroup>>({});

  /**
   * When the sheet opens (or the ledger rows change while open), resolve each
   * row's ribbon by fetching + deriving its first-swum workout. In practice
   * the firehoseCache already holds these from the history scan, so it's a
   * synchronous-resolution Promise.resolve() per UUID.
   */
  $effect(() => {
    if (!open) return;
    const rows: LedgerRow[] = $setsDiscoveredLedger;
    const seen = new Set<string>();
    for (const row of rows) {
      const uuid = row.firstSwumWorkoutId;
      if (!uuid || seen.has(uuid)) continue;
      seen.add(uuid);
      bridge
        .getWorkoutFirehose({ workoutUUID: uuid })
        .then(async (fh) => {
          if (!fh) return;
          const dw = await deriveWithSavedLabels(fh);
          // Fold all SetGroups from this workout into resolvedSetGroups.
          // A single workout may have multiple named sets (e.g. rockalita + warmup).
          const next: Record<string, SetGroup> = {};
          for (const sg of dw.setGroups) {
            if (sg.setId) {
              next[sg.setId] = sg;
            }
          }
          // Merge into existing map so rows resolved earlier are not dropped.
          resolvedSetGroups = { ...resolvedSetGroups, ...next };
        })
        .catch(() => {
          /* firehose unavailable — row silently degrades to text-only */
        });
    }
  });
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Sets you've swum" use:sheetDrag={close}>
    <div class="content">
      <header class="hdr">
        <h1>Sets you've swum</h1>
        <button type="button" class="close" onclick={close} aria-label="Close">Done</button>
      </header>

      {#if $setsDiscoveredLedger.length === 0}
        <p class="zero">Swim sets you've swum will appear here.</p>
      {:else}
        <!-- Reframes a ribbon as a picture of one swim (not a count), so a short
             2-chip set no longer reads as a render bug. When a single-stroke band
             is on screen, switch to band-aware copy. -->
        <p class="ribbon-key">
          {#if hasSingleStroke}
            Each band shows the strokes in one swim.
          {:else}
            Each ribbon shows a set's lengths, colored by stroke.
          {/if}
        </p>
        <ul class="stroke-legend" aria-label="What the ribbon colors mean">
          {#each ["Freestyle", "Backstroke", "Breaststroke", "Butterfly"] as stroke (stroke)}
            <li class="legend-item">
              <span class="legend-dot" style:background={strokeColor(stroke)}></span>
              <span class="legend-name">{stroke}</span>
            </li>
          {/each}
        </ul>
        <ul>
          {#each $setsDiscoveredLedger as row (row.setId)}
            {@const sg = row.isSignature ? resolvedSetGroups[row.setId] : undefined}
            {@const h = history[row.setId]}
            <li>
              <div
                class="row"
                role="button"
                tabindex="0"
                aria-label={`${row.displayName}, ${row.count} ${row.count === 1 ? "swim" : "swims"}. Tap to open.`}
                use:tapGuard={() => openDrill(row.setId)}
                onkeydown={(e) => onRowKey(e, row.setId)}
              >
                <div class="row-main">
                  {#if sg}
                    <!-- PRIMARY: color stroke-ribbon (the set's visual identity).
                         The single-stroke gem collapses to one band + ×N count. -->
                    <div class="ribbon-wrap">
                      <SetGroupRibbon
                        setGroup={sg}
                        singleBand={row.setId === SINGLE_STROKE_SET_ID}
                      />
                    </div>
                  {/if}
                  <!-- SECONDARY: name + count + first swum date -->
                  <div class="name">
                    <span class="name-text">{row.displayName}</span>
                    {#if row.isSignature}
                      <span class="sig" title="Signature set" aria-label="Signature set">◆</span>
                    {/if}
                  </div>
                  <p class="meta">
                    {row.count} {row.count === 1 ? "swim" : "swims"} · First: {fmtMonthYear(row.firstSwumDate)}
                  </p>
                  {#if h}
                    <p class="times">{timesLine(h)}</p>
                  {/if}
                </div>
                <!-- Subtle tap affordance (blind panel: rows read as un-tappable). -->
                <span class="row-chevron" aria-hidden="true">›</span>
              </div>
            </li>
          {/each}
        </ul>
        <p class="nudge">More sets show up here as you swim them.</p>
      {/if}
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    max-height: 85vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: var(--bg-canvas);
    border-radius: 20px 20px 0 0;
    animation: sheet-in var(--dur-card) var(--ease-swim);
  }
  .content {
    max-width: 430px;
    margin: 0 auto;
    padding: var(--space-4) var(--space-4)
      calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
  }

  .hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }
  .hdr h1 {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: var(--text-primary);
    margin: 0;
  }
  /* Big, obvious dismiss — 17px floor, wet-hands target. */
  .close {
    flex-shrink: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--accent-primary);
    background: var(--bg-raised);
    border: none;
    border-radius: 12px;
    padding: 10px 16px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .zero {
    font-size: 17px;
    color: var(--text-muted);
    line-height: 1.4;
    padding: var(--space-5) 0;
    margin: 0;
  }

  /* Muted helper line under the title — reframes each ribbon as one swim's picture
     (same muted family as the per-row "N swims · First:" lines). */
  .ribbon-key {
    margin: 0 0 var(--space-3);
    font-size: 15px;
    color: var(--text-muted);
    line-height: 1.35;
  }

  .stroke-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.9rem;
    margin: 0.5rem 0 0.75rem;
    padding: 0;
    list-style: none;
  }
  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 14px;
    color: var(--text-muted);
  }
  /* Round, matching the Swim tab's stroke-key dot (SwimHeroCard .key-dot) —
     persona-gate flagged the two legends using different swatch shapes. */
  .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex: none;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    border-bottom: 1px solid var(--divider);
  }
  .row {
    /* 44px min tap target (even when ribbon is absent). */
    min-height: 44px;
    padding: var(--space-3) 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    /* Lay the content column and the right-edge chevron on one row. */
    display: flex;
    align-items: center;
    gap: var(--space-3);
    border-radius: var(--radius-button);
    transition: background-color var(--dur-micro) var(--ease-swim);
  }
  .row:active {
    background: var(--bg-raised);
  }
  .row:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .row-main {
    flex: 1;
    min-width: 0;
  }
  /* Muted, right-aligned tap chevron — signals the row is tappable. */
  .row-chevron {
    flex-shrink: 0;
    font-size: 22px;
    line-height: 1;
    color: var(--text-dim);
    font-weight: 400;
  }

  /* Ribbon chip row spacing above the name line. */
  .ribbon-wrap {
    margin-bottom: 8px;
  }

  .name {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .name-text {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-primary);
  }
  /* Subtle signature marker. */
  .sig {
    font-size: 13px;
    color: var(--accent-primary);
    opacity: 0.8;
  }
  .meta {
    margin: 4px 0 0;
    font-size: 15px;
    color: #C5C6CC;
    font-variant-numeric: tabular-nums;
  }
  .times {
    margin: 2px 0 0;
    font-size: 14px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }

  /* Static, muted nudge — an invitation, not a goal. */
  .nudge {
    margin-top: var(--space-4);
    text-align: center;
    font-size: 15px;
    color: var(--text-dim);
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(8%); opacity: 0.6; }
    to { transform: translateY(0); opacity: 1; }
  }
</style>
